import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import IconSvg from "src/components/common/ui/IconSvg";

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'auto';
    };
  }, [isMobileMenuOpen]);

  const effectiveDate = "September 22, 2025";
  const lastUpdated = "September 22, 2025";

  return (
    <>
      <div className="lg:w-[1317px] lg:mx-auto lg:pt-10 pt-5 lg:pb-20 pb-0 lg:px-0 px-4">
        {/* Header */}
        <div className="flex items-center justify-between relative">
          <IconSvg icon="appLogo" className="cursor-pointer lg:h-auto h-8" onClick={() => navigate('/')} />

          {/* Mobile Menu Button */}
          <div
            className="lg:hidden block cursor-pointer z-50"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <></>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="33"
                viewBox="0 0 32 33"
                fill="none"
                className="transition-transform duration-300"
              >
                <path
                  d="M4 23.1666H28M4 16.5H28M4 9.83331H28"
                  stroke="black"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>

          {/* Mobile Menu Overlay and Content */}
          <div className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
            {/* Background Overlay */}
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              onClick={() => setIsMobileMenuOpen(false)}
            ></div>

            {/* Menu Content */}
            <div
              ref={mobileMenuRef}
              className={`absolute top-0 right-0 h-full w-80 bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
            >
              <div className="flex flex-col h-full p-6">
                {/* Close Button */}
                <div className="flex justify-end mb-8">
                  <button
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M18 6L6 18M6 6L18 18"
                        stroke="black"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>

                {/* Navigation Links */}
                <div className="flex flex-col space-y-6 mb-8">
                  <p className="text-xl font-medium text-grayscale-800 cursor-pointer font-inter py-2 border-b border-gray-100">
                    About
                  </p>
                  <p className="text-xl font-medium text-grayscale-800 cursor-pointer font-inter py-2 border-b border-gray-100">
                    Testimonial
                  </p>
                  <p className="text-xl font-medium text-grayscale-800 cursor-pointer font-inter py-2 border-b border-gray-100">
                    Contact
                  </p>
                </div>

                {/* Auth Buttons */}
                <div className="mt-auto mb-8">
                  <button
                    type="button"
                    onClick={() => {
                      navigate('/signin');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-6 py-4 rounded-lg cursor-pointer text-grayscale-100 bg-black text-lg font-medium font-inter mb-4 transition-transform hover:scale-105"
                  >
                    Sign in
                  </button>
                  <button
                    onClick={() => {
                      navigate('/signup');
                      setIsMobileMenuOpen(false);
                    }}
                    type="button"
                    className="w-full px-6 py-4 rounded-lg cursor-pointer text-grayscale-100 bg-[#6277FF] text-lg font-medium font-inter transition-transform hover:scale-105"
                  >
                    Sign up
                  </button>
                </div>

                {/* Footer Note */}
                <div className="text-center text-gray-500 text-sm">
                  © 2024 Adeasy AI. All rights reserved.
                </div>
              </div>
            </div>
          </div>

          <div className="lg:flex hidden items-center space-x-32">
            <div className="flex items-center space-x-20">
              <p className="text-[22px] font-normal text-grayscale-800 cursor-pointer font-inter">
                About
              </p>
              <p className="text-[22px] font-normal text-grayscale-800 cursor-pointer font-inter">
                Testimonial
              </p>
              <p className="text-[22px] font-normal text-grayscale-800 cursor-pointer font-inter">
                Contact
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                type="button"
                onClick={() => navigate('/signin')}
                className="px-6 py-3 rounded-lg cursor-pointer text-grayscale-100 bg-black text-lg font-medium font-inter w-[154px]"
              >
                Sign in
              </button>
              <button
                onClick={() => navigate('/signup')}
                type="button"
                className="px-6 py-3 rounded-lg cursor-pointer text-grayscale-100 bg-[#6277FF] text-lg font-medium font-inter w-[154px]"
              >
                Sign up
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 mt-8 space-y-8">
          {/* Introduction */}
          <section>
            <div className="text-center">
              <h1 className="text-6xl font-bold mb-4" style={{ fontSize: '36px' }}>Privacy Policy</h1>            </div>
            <p className="text-gray-700">
              <strong>Effective Date:</strong> {effectiveDate}<br />
              <strong>Last Updated:</strong> {lastUpdated}
            </p>
            <p className="mt-4 text-gray-700">
              This Privacy Policy explains how <strong>Adeasy</strong> (“we,” “our,” or “us”), owned and operated by <strong>LVRG Group Inc.</strong>, collects, uses, shares, and safeguards information when you use our website <strong>adeasy.ai</strong>, related applications, and services (collectively, the “Services”).
              By using <strong>Adeasy</strong>, you agree to the practices described in this Privacy Policy. If you do not agree, you must discontinue use of our Services.
            </p>

          </section>

          {/* 1. Information We Collect */}
          <section>
            <h2 className="text-lg font-semibold mb-2">1. Information We Collect</h2>
            <p className="mb-2">We may collect the following categories of information:</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-gray-700">
              <h2 className="text-lg font-semibold mb-2">Information You Provide Directly</h2>
              <li><strong>Account Information</strong> – Name, email address, phone number, password, company name, billing address.</li>
              <li><strong>Payment Information</strong> – Credit/debit card details and related billing information (processed by third-party providers; we do not store full payment details).</li>
              <li><strong>Communications</strong> – Customer support requests, feedback, survey responses, or other communications.</li>
            </ul>
            <br />
            <p className="text-lg font-semibold mb-2">Information We Collect Automatically</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-gray-700">
              <li><strong>Usage Data</strong>  – IP address, device identifiers, browser type, operating system, referring URLs, pages viewed, time spent, clicks, and interactions.</li>
              <li><strong>Cookies & Tracking</strong> – We use cookies, pixels, and similar technologies to track activity, improve user experience, and analyze traffic.</li>
              <li><strong>Log Data</strong>  – Server logs may capture date/time of access, errors, and performance metrics.</li>
            </ul>

            <p className="text-lg font-semibold mb-2">Information from Third Parties</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-gray-700">
              <li><strong>Meta & Other Integrations</strong> – When you connect your advertising accounts (e.g., Facebook, Instagram), we access campaign data, ad performance metrics, and account identifiers per your authorization.</li>
              <li><strong>Partners & Vendors</strong> – We may receive information from business partners, resellers, or analytics providers.</li>
            </ul>
          </section>

          {/* 2. How We Use Your Information */}
          <section>
            <h2 className="text-lg font-semibold mb-2">2. How We Use Your Information</h2>
            <p>We use your information to:</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-gray-700">
              <li>Provide, operate, and improve the Services.</li>
              <li>Process transactions and billing.</li>
              <li>Enable integrations with Meta and other platforms.</li>
              <li>Deliver insights, recommendations, and analytics.</li>
              <li>Personalize your experience and optimize campaign performance.</li>
              <li>Communicate with you about updates, promotions, and customer support.</li>
              <li>Detect, investigate, and prevent fraud, abuse, or security incidents.</li>
              <li>Comply with legal obligations and enforce our Terms of Service.</li>
            </ul>
          </section>

          {/* 3. How We Share Your Information */}
          <section>
            <h2 className="text-lg font-semibold mb-2">3. How We Share Information</h2>
            <p>We do not sell your personal information. We may share it as follows:</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-gray-700">
              <li><strong>Service Providers</strong>  – Vendors who perform services such as hosting, payments, analytics, and customer support.</li>
              <li><strong> Meta Platforms</strong> – Data you authorize us to access is shared with Meta to manage your advertising campaigns.</li>
              <li><strong>Business Transfers</strong>  – In a merger, acquisition, or sale of assets, information may be transferred to the new owner.</li>
              <li><strong>Legal Compliance</strong>  – Where required to comply with laws, regulations, subpoenas, or lawful requests.</li>
              <li><strong>With Consent</strong>  – We may share information when you explicitly consent.</li>
            </ul>
          </section>


          <section>
            <h2 className="text-lg font-semibold mb-2">4. Cookies & Tracking Technologies</h2>
            <p>We use cookies, pixels, tags, and similar technologies for:</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-gray-700">
              <li>Authentication and login sessions.</li>
              <li>Saving preferences and settings.</li>
              <li>Analytics (e.g., Google Analytics).</li>
              <li>Advertising and retargeting.</li>
            </ul>
            <br />
            <p>You can control cookies through your browser settings. Disabling cookies may limit functionality of the Services.</p>
          </section>

          {/* 5. Data Retention */}
          <section>
            <h2 className="text-lg font-semibold mb-2">5. Data Retention</h2>
            <p>We use administrative, technical, and physical safeguards to protect data, including:</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-gray-700">
              <li>Encryption in transit and at rest where appropriate.</li>
              <li>Access controls and authentication.</li>
              <li>Regular monitoring and testing.</li>
            </ul>
            <p className="mt-2">No method of transmission or storage is completely secure, and we cannot guarantee absolute security.</p>
          </section>

          {/* 6. Data Security */}
          <section>
            <h2 className="text-lg font-semibold mb-2">6. Data Security</h2>
            <p>We use administrative, technical, and physical safeguards to protect data, including:</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-gray-700">
              <li>Encryption in transit and at rest where appropriate.</li>
              <li>Access controls and authentication.</li>
              <li>Regular monitoring and testing.</li>
            </ul>
            <p className="mt-2">No method of transmission or storage is completely secure, and we cannot guarantee absolute security.</p>
          </section>

          {/* 7. International Data Transfers */}
          <section>
            <h2 className="text-lg font-semibold mb-2">7. International Data Transfers</h2>
            <p className="text-gray-700">Your information may be processed in countries outside your place of residence, including <strong> Canada and the United States.</strong></p>
            <p className="text-gray-700">Where required, we rely on lawful transfer mechanisms such as <strong> Standard Contractual Clauses (SCCs) </strong> under <strong>GDPR.</strong></p>
          </section>

          {/* 8. Your Rights */}
          <section>
            <h2 className="text-lg font-semibold mb-2">8. Your Rights</h2>
            <p className="mb-2">Depending on your location, you may have rights under GDPR, CCPA, or similar laws:</p>
            <ul className="list-disc list-inside ml-4 mt-2 space-y-1 text-gray-700">
              <li><strong>Access & Portability</strong>  – Request a copy of your personal data.</li>
              <li><strong>Correction</strong>  – Request corrections to inaccurate or incomplete data.</li>
              <li><strong>Deletion</strong>  – Request deletion of your personal data.</li>
              <li><strong>Restriction</strong> – Limit how your data is processed.</li>
              <li><strong>Objection</strong> – Object to processing (e.g., for marketing).</li>
              <li><strong>Withdrawal of Consent</strong> – Withdraw consent for processing at any time.</li>
            </ul>
            <p className="mt-2">To exercise rights, contact us at info@adeasy.ai. We may require verification of identity.</p>
          </section>

          {/* 9. Children's Privacy */}
          <section>
            <h2 className="text-lg font-semibold mb-2">9. Children&apos;s Privacy</h2>
            <p>Our Services are not directed to children under 13 (or 16 where required by local law). We do not knowingly collect data from children. If we become aware of such collection, we will delete it.</p>
          </section>

          {/* 10. Third-Party Services */}
          <section>
            <h2 className="text-lg font-semibold mb-2">10. Third-Party Services</h2>
            <p>Our Services may link to or integrate with third-party platforms (such as Meta). We are not responsible for their privacy practices. We encourage you to review their privacy policies.</p>
          </section>

          {/* 11. Changes to This Policy */}
          <section>
            <h2 className="text-lg font-semibold mb-2">11. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. Updates will be posted with a new “Last Updated” date. Significant changes may be communicated via email or in-app notifications.</p>
          </section>

          {/* 12. Contact Us */}
          <section>
            <h2 className="text-lg font-semibold mb-2">12. Contact Us</h2>
            <p>If you have questions about this Privacy Policy or your rights, contact us at:</p>
            <p className="mt-2 font-medium">LVRG Group Inc</p>
            <p>Email: <a href="mailto:info@adeasy.ai" className="underline">info@adeasy.ai</a></p>
            <p>
              Website:{" "}
              <a
                href="https://adeasy.ai"
                className="text-blue-600 underline"
              >
                https://adeasy.ai
              </a>
            </p>

          </section>
        </main>
      </div>

      {/* Footer */}
      <div
        className="lg:h-[100px] bg-cover bg-center"
        style={{
          backgroundImage: "url('/assets/images/footer.png')",
        }}
      >
        <div className="lg:w-[1317px] lg:mx-auto flex items-center justify-between py-6 lg:px-0 px-4">
          {/* Logo */}
          <img
            src="/assets/images/footer-logo.png"
            onClick={() => navigate('/')}
            alt="Logo"
            className="lg:h-12 h-8 cursor-pointer"
          />

          {/* Navigation Links */}
          <div className="flex items-center lg:space-x-16 space-x-8">
            <p className="text-white lg:text-[22px] text-sm font-normal cursor-pointer font-inter">
              About
            </p>
            <p className="text-white lg:text-[22px] text-sm font-normal cursor-pointer font-inter">
              Testimonial
            </p>
            <p className="text-white lg:text-[22px] text-sm font-normal cursor-pointer font-inter"onClick={() => navigate('/contact-us')}>
              Contact
            </p>
            <p className="text-white lg:text-[22px] text-sm font-normal cursor-pointer font-inter"
              onClick={() => navigate('/privacy-policy')}
            >
              Privacy Policy
            </p>
          </div>

          {/* Sign In Button */}
          <div className="lg:block hidden">
            <button
              type="button"
              onClick={() => navigate("/signin")}
              className="px-6 py-2 rounded-lg bg-white text-black text-[18px] font-medium font-inter shadow-md hover:bg-gray-200 transition"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center py-4 space-x-2">
        <p className="font-inter lg:text-lg text-sm lg:font-medium font-normal text-black">
          Copyright 2024
        </p>
        <IconSvg icon='copyright' />
      </div>
    </>
  );
}